<template>
  <button
    v-if="layout === 'chip'"
    type="button"
    class="mode-chip"
    :class="[tone, modeClass]"
    :title="t('duel.response.hint')"
    :aria-label="t('duel.response.hint')"
    @click="duelPrefs.cycleResponseMode()"
  >
    <span class="mode-kicker">{{ t('duel.response.title') }}</span>
    <span class="mode-value">{{ t(`duel.response.${duelPrefs.responseMode}`) }}</span>
  </button>

  <div v-else class="mode-prefs" :class="tone">
    <p class="pref-name">{{ t('settings.responseMode') }}</p>
    <p class="pref-hint">{{ t('settings.responseModeHint') }}</p>
    <div class="mode-seg">
      <button
        v-for="mode in RESPONSE_MODE_ORDER"
        :key="mode"
        type="button"
        class="mode-opt"
        :class="{ on: duelPrefs.responseMode === mode }"
        :aria-pressed="duelPrefs.responseMode === mode"
        @click="duelPrefs.setResponseMode(mode)"
      >
        {{ t(`duel.response.${mode}`) }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RESPONSE_MODE_ORDER, useDuelPrefsStore } from '../stores/duelPrefs'
import { useI18n } from '../i18n/t'

withDefaults(
  defineProps<{
    layout?: 'chip' | 'prefs'
    tone?: 'duel' | 'menu'
  }>(),
  {
    layout: 'chip',
    tone: 'duel',
  },
)

const { t } = useI18n()
const duelPrefs = useDuelPrefsStore()

const modeClass = computed(() => `is-${duelPrefs.responseMode}`)
</script>

<style scoped>
.mode-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  min-width: 64px;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid var(--md-sys-color-outline-variant);
  background: var(--md-sys-color-surface-container);
  color: var(--md-sys-color-on-surface);
  font-family: Roboto, system-ui, sans-serif;
  cursor: pointer;
  pointer-events: auto;
}

.mode-chip.duel {
  background: rgba(12, 14, 24, 0.88);
  border-color: rgba(232, 197, 71, 0.32);
  color: #fde68a;
}

.mode-kicker {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.72;
}

.mode-value {
  font-size: 12px;
  font-weight: 700;
}

.mode-chip.is-always.duel {
  border-color: rgba(96, 165, 250, 0.55);
  color: #bfdbfe;
}

.mode-chip.is-minimal.duel {
  border-color: rgba(45, 212, 191, 0.45);
  color: #99f6e4;
}

.mode-chip.is-off.duel {
  border-color: rgba(148, 163, 184, 0.45);
  color: #cbd5e1;
}

.mode-prefs .pref-name {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #f8fafc;
}

.mode-prefs .pref-hint {
  margin: 2px 0 10px;
  font-size: 11px;
  line-height: 15px;
  font-weight: 400;
  color: #94a3b8;
}

.mode-prefs.menu .pref-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--md-sys-color-on-surface);
}

.mode-prefs.menu .pref-hint {
  font-size: 13px;
  line-height: 18px;
  color: var(--md-sys-color-on-surface-variant);
}

.mode-seg {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.mode-opt {
  flex: 1 1 4.5rem;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid var(--md-sys-color-outline-variant);
  background: var(--md-sys-color-surface-container);
  color: var(--md-sys-color-on-surface-variant);
  font-family: Roboto, system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.mode-opt.on {
  background: var(--md-sys-color-secondary-container);
  color: var(--md-sys-color-on-secondary-container);
  border-color: transparent;
}
</style>
